package com.sddamico.draftmachine.ds.feedback

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.motion.LocalExiting
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div

enum class ToastStackPosition { TopRight, TopCenter, BottomRight, BottomCenter }

private fun ToastStackPosition.reversed(): Boolean =
  this == ToastStackPosition.BottomRight || this == ToastStackPosition.BottomCenter

private fun ToastStackPosition.centered(): Boolean =
  this == ToastStackPosition.TopCenter || this == ToastStackPosition.BottomCenter

/** Anchor edge + alignment for the fixed rack, mirroring the JSX POSITIONS map. */
private fun Modifier.anchor(position: ToastStackPosition): Modifier {
  val top = position == ToastStackPosition.TopRight || position == ToastStackPosition.TopCenter
  var mod = if (top) this.top(DmSpacing.space5) else this.bottom(DmSpacing.space5)
  mod = if (position.centered()) {
    mod.left(50.percent).styleModifier { property("transform", "translateX(-50%)") }
      .alignItems(AlignItems.Center)
  } else {
    mod.right(DmSpacing.space5).alignItems(AlignItems.FlexEnd)
  }
  return mod
}

/**
 * The pneumatic message rack — a fixed corner where [Toast]s stack, newest nearest the edge. One
 * per app; wrap each toast in a [ToastStackItem]. Port of `components/feedback/ToastStack.jsx`.
 *
 * React's `Children.map` wraps every child automatically; a Compose content lambda can't be
 * iterated, so callers place each toast inside [ToastStackItem] to get the rise-in + pointer seam.
 */
@Composable
fun ToastStack(
  position: ToastStackPosition = ToastStackPosition.BottomRight,
  modifier: Modifier = Modifier,
  content: @Composable () -> Unit,
) {
  Div(
    attrs = modifier
      .position(Position.Fixed)
      .displayFlex()
      .flexDirection(if (position.reversed()) FlexDirection.ColumnReverse else FlexDirection.Column)
      .gap(DmSpacing.space3)
      .anchor(position)
      .styleModifier { property("z-index", "200"); property("pointer-events", "none") }
      .toAttrs()
  ) {
    content()
  }
}

/**
 * A single slot in a [ToastStack]: restores pointer events and plays the slip's arrival. Inside a
 * [Presence][com.sddamico.draftmachine.ds.motion.Presence] it also plays the departure.
 */
@Composable
fun ToastStackItem(
  modifier: Modifier = Modifier,
  content: @Composable () -> Unit,
) {
  Div(attrs = modifier.toastMotion().toAttrs()) {
    content()
  }
}

/**
 * A message slip's motion: it pops up on arrival with a little pneumatic overshoot, and sinks away
 * while its [Presence][com.sddamico.draftmachine.ds.motion.Presence] is exiting.
 */
@Composable
fun Modifier.toastMotion(): Modifier {
  val exiting = LocalExiting.current
  return styleModifier {
    property("pointer-events", if (exiting) "none" else "auto")
    property(
      "animation",
      if (exiting) "dm-toast-out ${DmMotion.overlayExitMs}ms ${DmMotion.easeExit} both"
      else "dm-toast-in 460ms ${DmMotion.easeSpring} both",
    )
  }
}
